import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { tagColour } from '../LogTypePopover';
import Avatar from './Avatar';
import { EpicSummaryPerson, LogTypeHours } from './types';
import * as fmt from '../../lib/format';

const SHOWN = 10;

function TagChips({ tags }: { tags: LogTypeHours[] }) {
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => {
        const label = t.log_type ?? 'Untagged';
        const colour = t.log_type ? tagColour(t.log_type) : 'bg-gray-50 text-gray-500';
        return (
          <span key={label} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ${colour}`}>
            {label}
            <span className="tabular-nums opacity-80">{fmt.hours(t.hours)}</span>
          </span>
        );
      })}
    </div>
  );
}

function PersonRow({ person, max }: { person: EpicSummaryPerson; max: number }) {
  const [open, setOpen] = useState(false);
  const canOpen = person.by_log_type.length > 0;
  const quiet = person.hours_period === 0;
  const width = max > 0 ? Math.max(person.hours_period > 0 ? 2 : 0, (person.hours_period / max) * 100) : 0;
  return (
    <li>
      <button
        type="button"
        onClick={() => canOpen && setOpen((o) => !o)}
        aria-expanded={canOpen ? open : undefined}
        disabled={!canOpen}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 enabled:hover:bg-gray-50/70 enabled:active:bg-gray-100/70 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 disabled:cursor-default"
      >
        <Avatar name={person.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium text-gray-900">{person.name}</span>
            <span className={`shrink-0 text-sm font-semibold tabular-nums ${quiet ? 'text-gray-500' : 'text-gray-900'}`}>
              {quiet ? 'No time this period' : fmt.hours(person.hours_period)}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden>
            <div className="h-full rounded-full bg-blue-500 transition-[width] duration-500 ease-out motion-reduce:transition-none" style={{ width: `${width}%` }} />
          </div>
          <div className="mt-1 flex justify-between gap-3 text-xs text-gray-500">
            <span className="tabular-nums">{fmt.hours(person.hours_to_date)} to date</span>
            {person.assigned > 0 && <span>{person.assigned} assigned</span>}
          </div>
        </div>
        {canOpen && (
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ease-out motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        )}
      </button>
      {open && canOpen && (
        <div className="px-4 pb-3.5 pl-[3.75rem]">
          <p className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">This period, by activity</p>
          <TagChips tags={person.by_log_type} />
        </div>
      )}
    </li>
  );
}

interface Props {
  people: EpicSummaryPerson[];
  byLogType: LogTypeHours[];
}

// Who put time or ownership into the epic, busiest first. Each person opens to show what
// kind of work the hours were.
export default function PeopleList({ people, byLogType }: Props) {
  const [all, setAll] = useState(false);
  if (people.length === 0) {
    return <p className="px-4 py-8 text-center text-sm text-gray-500">No one has logged time or holds a ticket here.</p>;
  }
  const max = Math.max(0, ...people.map((p) => p.hours_period));
  const list = all ? people : people.slice(0, SHOWN);
  return (
    <div>
      {byLogType.length > 0 && (
        <div className="border-b border-gray-100 px-4 py-3.5">
          <p className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Everyone, by activity</p>
          <TagChips tags={byLogType} />
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {list.map((p) => (
          <PersonRow key={p.account_id} person={p} max={max} />
        ))}
      </ul>
      {people.length > SHOWN && !all && (
        <div className="border-t border-gray-100 p-2">
          <button
            type="button"
            onClick={() => setAll(true)}
            className="w-full rounded-xl px-3 py-2 text-sm font-medium text-blue-700 transition-[background-color,transform] duration-150 hover:bg-blue-50/70 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Show all {people.length} people
          </button>
        </div>
      )}
    </div>
  );
}
